type LibraryLanguageCode = "en" | "es" | "fr" | "nl";
type LibraryCategory =
  | "opinion"
  | "story"
  | "explain"
  | "persuade"
  | "reflect"
  | "everyday"
  | "work"
  | "imagine";

type LocalizedPromptSeed = {
  slug: string;
  category: LibraryCategory;
  text: Record<LibraryLanguageCode, string>;
};

/**
 * Prompts that exist in every launch language, expanded one-per-language by
 * `buildAdditionalImpromptuPrompts` and appended to the core set in `config.ts`.
 *
 * There were 54 seeds here, which gave each language 64 prompts. That number was
 * the problem rather than the achievement: a learner who has fifteen seconds to
 * think does not benefit from a sixty-fourth option, and volume had been reached
 * by writing the same task several ways. Four of the six `opinion` seeds were a
 * question about working hours ("remote-work", "four-day-week", plus the core
 * meeting-free-day prompt); `problem-solved` and the core useful-mistake prompt
 * asked for the same anecdote; `learn-new-skill` and `attend-event` were the core
 * "convince a friend to try an activity" prompt with the noun changed. Every one
 * of those has to be translated four times, reviewed four times, and is one more
 * thing between someone and speaking.
 *
 * 22 remain, chosen against four tests. A prompt survives if it can be answered
 * in sixty seconds by a beginner, if it gives the rubric something to grade (a
 * claim to support, a sequence to follow) rather than one beat to state, if it is
 * not a near-duplicate of a core prompt or another seed, and if it assumes no life
 * context a learner may not have — which is why the school-uniform debate and
 * "how would you improve a team meeting" are gone, along with the rest of the
 * stock ESL-worksheet set.
 *
 * The shape of what is left is deliberate: two seeds in each of the five
 * categories the core set also covers, four in each of `everyday`, `work` and
 * `imagine`, which have no core prompts at all. That lands every category on four
 * per language. Do not empty those three — PromptStudio renders a filter chip per
 * category unconditionally and both `chooseCategory` and `shuffle` return silently
 * on an empty result, so a category with no prompts is a chip that does nothing.
 *
 * Deleting a seed retires the prompt ids built from its slug, which appear as a
 * property on historical PostHog events. Those events keep their recorded value;
 * nothing joins on them. Renaming a slug would be worse than deleting it, because
 * it silently splits one prompt's history across two ids.
 */
const ADDITIONAL_PROMPT_SEEDS: ReadonlyArray<LocalizedPromptSeed> = [
  {
    slug: "phones-at-meals",
    category: "opinion",
    text: {
      en: "Should phones stay off the table during meals with other people? Why or why not?",
      es: "¿Deberían dejarse los teléfonos fuera de la mesa durante las comidas con otras personas? ¿Por qué?",
      fr: "Faut-il laisser les téléphones loin de la table pendant les repas à plusieurs ? Pourquoi ?",
      nl: "Moeten telefoons van tafel blijven als je met anderen eet? Waarom wel of niet?",
    },
  },
  {
    slug: "cashless-life",
    category: "opinion",
    text: {
      en: "Is a cashless society convenient or risky? Make the strongest case for your view.",
      es: "¿Es cómoda o arriesgada una sociedad sin efectivo? Defiende tu opinión con tu mejor argumento.",
      fr: "Une société sans argent liquide est-elle pratique ou risquée ? Défendez votre point de vue.",
      nl: "Is een samenleving zonder contant geld handig of riskant? Geef je sterkste argument.",
    },
  },
  {
    slug: "plan-changed",
    category: "story",
    text: {
      en: "Tell a story about a plan that changed at the last minute.",
      es: "Cuenta una historia sobre un plan que cambió en el último momento.",
      fr: "Racontez une histoire à propos d'un projet qui a changé au dernier moment.",
      // "Vertel over" is idiomatic but means "talk about", which drops the framing
      // the English asks for. These are `story` prompts and the rubric judges
      // whether a listener could follow the sequence, so the Dutch has to ask for
      // a story too rather than for a topic.
      nl: "Vertel het verhaal van een plan dat op het laatste moment veranderde.",
    },
  },
  {
    slug: "lost-object",
    category: "story",
    text: {
      en: "Tell the story of something you lost and how the situation ended.",
      es: "Cuenta la historia de algo que perdiste y cómo terminó la situación.",
      fr: "Racontez l'histoire d'un objet que vous avez perdu et comment la situation s'est terminée.",
      nl: "Vertel het verhaal van iets dat je bent kwijtgeraakt en hoe het afliep.",
    },
  },
  {
    slug: "hobby-for-beginner",
    category: "explain",
    text: {
      en: "Explain how a complete beginner could start a hobby you know well.",
      es: "Explica cómo un principiante absoluto podría empezar una afición que conoces bien.",
      fr: "Expliquez comment un débutant complet pourrait commencer une activité que vous connaissez bien.",
      nl: "Leg uit hoe een complete beginner kan starten met een hobby die jij goed kent.",
    },
  },
  {
    slug: "your-neighbourhood",
    category: "explain",
    text: {
      en: "Explain your neighbourhood to someone who is thinking about moving there.",
      es: "Describe tu barrio a alguien que está pensando en mudarse allí.",
      fr: "Présentez votre quartier à quelqu'un qui envisage de s'y installer.",
      nl: "Leg je buurt uit aan iemand die overweegt erheen te verhuizen.",
    },
  },
  {
    slug: "choose-local-shop",
    category: "persuade",
    text: {
      en: "Persuade someone to choose a local shop instead of a large chain.",
      es: "Convence a alguien de elegir una tienda local en lugar de una gran cadena.",
      fr: "Convainquez quelqu'un de choisir un commerce local plutôt qu'une grande chaîne.",
      nl: "Overtuig iemand om voor een lokale winkel te kiezen in plaats van een grote keten.",
    },
  },
  {
    slug: "repair-not-replace",
    category: "persuade",
    text: {
      en: "Persuade people to repair one type of item instead of replacing it.",
      es: "Convence a la gente de reparar un tipo de objeto en lugar de reemplazarlo.",
      fr: "Convainquez les gens de réparer un type d'objet au lieu de le remplacer.",
      nl: "Overtuig mensen om een bepaald voorwerp te repareren in plaats van te vervangen.",
    },
  },
  {
    slug: "advice-kept",
    category: "reflect",
    text: {
      en: "What piece of advice do you still think about, and why did it stay with you?",
      es: "¿En qué consejo sigues pensando y por qué se te quedó grabado?",
      fr: "À quel conseil pensez-vous encore, et pourquoi vous a-t-il marqué ?",
      nl: "Aan welk advies denk je nog steeds, en waarom is het je bijgebleven?",
    },
  },
  {
    slug: "meaningful-object",
    category: "reflect",
    text: {
      en: "Which ordinary object means more to you than its price suggests?",
      es: "¿Qué objeto cotidiano significa para ti más de lo que su precio sugiere?",
      fr: "Quel objet ordinaire a pour vous une valeur bien supérieure à son prix ?",
      nl: "Welk gewoon voorwerp betekent meer voor je dan de prijs doet vermoeden?",
    },
  },
  {
    slug: "morning-routine",
    category: "everyday",
    text: {
      en: "Walk someone through your usual morning, including the part you would most like to change.",
      es: "Explica cómo es tu mañana habitual, incluida la parte que más te gustaría cambiar.",
      fr: "Décrivez votre matinée habituelle, y compris la partie que vous aimeriez le plus changer.",
      nl: "Neem iemand mee door je gewone ochtend, inclusief wat je het liefst zou veranderen.",
    },
  },
  {
    slug: "comfort-meal",
    category: "everyday",
    text: {
      en: "Describe a meal you turn to when you want comfort or familiarity.",
      es: "Describe una comida a la que recurres cuando buscas consuelo o algo familiar.",
      fr: "Décrivez un repas que vous choisissez lorsque vous recherchez du réconfort ou quelque chose de familier.",
      nl: "Beschrijf een maaltijd die je kiest als je behoefte hebt aan troost of iets vertrouwds.",
    },
  },
  {
    slug: "daily-journey",
    category: "everyday",
    text: {
      en: "Describe a journey you make often and one detail most people would miss.",
      es: "Describe un trayecto que haces a menudo y un detalle que la mayoría no notaría.",
      fr: "Décrivez un trajet que vous faites souvent et un détail que la plupart des gens ne remarqueraient pas.",
      nl: "Beschrijf een route die je vaak aflegt en een detail dat de meeste mensen missen.",
    },
  },
  {
    slug: "favourite-room",
    category: "everyday",
    text: {
      en: "Describe your favourite room or corner and explain why it works for you.",
      es: "Describe tu habitación o rincón favorito y explica por qué te gusta.",
      fr: "Décrivez votre pièce ou votre coin préféré et expliquez pourquoi vous vous y sentez bien.",
      nl: "Beschrijf je favoriete kamer of hoek en leg uit waarom die plek voor je werkt.",
    },
  },
  {
    slug: "receive-feedback",
    category: "work",
    text: {
      en: "Explain how you prefer to receive difficult feedback.",
      es: "Explica cómo prefieres recibir comentarios difíciles.",
      fr: "Expliquez comment vous préférez recevoir des commentaires difficiles.",
      nl: "Leg uit hoe je moeilijke feedback het liefst ontvangt.",
    },
  },
  {
    slug: "competing-priorities",
    category: "work",
    text: {
      en: "How would you handle two important tasks with the same deadline?",
      es: "¿Cómo gestionarías dos tareas importantes con la misma fecha límite?",
      fr: "Comment géreriez-vous deux tâches importantes ayant la même échéance ?",
      nl: "Hoe zou je twee belangrijke taken met dezelfde deadline aanpakken?",
    },
  },
  {
    slug: "explain-your-role",
    category: "work",
    text: {
      en: "Explain your work or studies to someone outside your field.",
      es: "Explica tu trabajo o tus estudios a alguien de otro ámbito.",
      fr: "Expliquez votre travail ou vos études à quelqu'un d'un autre domaine.",
      nl: "Leg je werk of studie uit aan iemand buiten jouw vakgebied.",
    },
  },
  {
    slug: "missed-deadline",
    category: "work",
    text: {
      en: "How would you tell someone that an important deadline will be missed?",
      es: "¿Cómo le dirías a alguien que no se cumplirá un plazo importante?",
      fr: "Comment annonceriez-vous à quelqu'un qu'une échéance importante ne sera pas respectée ?",
      nl: "Hoe zou je iemand vertellen dat een belangrijke deadline niet wordt gehaald?",
    },
  },
  {
    slug: "extra-hour",
    category: "imagine",
    text: {
      en: "Imagine every day had one extra hour. What would you do with it, and what would change?",
      es: "Imagina que cada día tuviera una hora extra. ¿Qué harías con ella y qué cambiaría?",
      fr: "Imaginez que chaque journée ait une heure de plus. Qu'en feriez-vous et qu'est-ce qui changerait ?",
      nl: "Stel je voor dat elke dag een extra uur had. Wat zou je ermee doen en wat zou veranderen?",
    },
  },
  {
    slug: "exchange-lives",
    category: "imagine",
    text: {
      en: "If you could exchange daily lives with someone for one week, who would you choose and why?",
      es: "Si pudieras intercambiar tu vida diaria con alguien durante una semana, ¿a quién elegirías y por qué?",
      fr: "Si vous pouviez échanger votre quotidien avec quelqu'un pendant une semaine, qui choisiriez-vous et pourquoi ?",
      nl: "Als je een week lang van dagelijks leven kon ruilen met iemand, wie zou je kiezen en waarom?",
    },
  },
  {
    slug: "phone-free-weekend",
    category: "imagine",
    text: {
      en: "Imagine a weekend without phones or internet. What would be easier, and what would be harder?",
      es: "Imagina un fin de semana sin teléfonos ni internet. ¿Qué sería más fácil y qué sería más difícil?",
      fr: "Imaginez un week-end sans téléphone ni internet. Qu'est-ce qui serait plus facile ou plus difficile ?",
      nl: "Stel je een weekend zonder telefoons of internet voor. Wat zou makkelijker en wat moeilijker zijn?",
    },
  },
  {
    slug: "tiny-business",
    category: "imagine",
    text: {
      en: "Imagine you could open a tiny business tomorrow. What would it offer and who would visit?",
      es: "Imagina que mañana pudieras abrir un pequeño negocio. ¿Qué ofrecería y quién iría?",
      fr: "Imaginez que vous puissiez ouvrir une petite entreprise demain. Que proposerait-elle et qui viendrait ?",
      nl: "Stel je voor dat je morgen een klein bedrijf kon openen. Wat zou het aanbieden en wie zou er komen?",
    },
  },
];

/**
 * Both durations arrive as arguments rather than being imported, because
 * `config.ts` imports this module and owns those constants. Reaching back for them
 * would make the cycle real, and writing the numbers again here would give the
 * seeded prompts a different prep time from the core ones the first time either
 * constant moved.
 */
export function buildAdditionalImpromptuPrompts(preparationSeconds: number, targetSeconds: number) {
  return ADDITIONAL_PROMPT_SEEDS.flatMap((seed) =>
    (Object.keys(seed.text) as LibraryLanguageCode[]).map((languageCode) => ({
      id: `${seed.category}-${seed.slug}-${languageCode}`,
      category: seed.category,
      languageCode,
      text: seed.text[languageCode],
      preparationSeconds,
      targetSeconds,
    }))
  );
}
